import { Users } from 'lucide-react';

interface Team {
  _id: string;
  name: string;
  isDefault: boolean;
  isUnassigned: boolean;
  deviceCount: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  createdAt?: string | null;
}

interface Props {
  teams: Team[];
  loading?: boolean;
}

/**
 * Team card grid for the staff customer drill-in — the customer's OptiSigns
 * teams (workspaces, `teams` collection), each with a per-team device rollup.
 * Distinct from StaffFleetGrid (mdm-fleets). Read-only.
 */
export function StaffTeamGrid({ teams, loading }: Props) {
  if (loading && !teams.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">{'Loading teams…'}</div>
    );
  }

  if (!teams.length) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">{'No teams found.'}</div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
      data-testid="staff-team-grid"
    >
      {teams.map((team) => (
        <TeamCard key={team._id} team={team} />
      ))}
    </div>
  );
}

function TeamCard({ team }: { team: Team }) {
  const badge = team.isDefault ? 'Default' : team.isUnassigned ? 'Unassigned' : null;
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid="staff-team-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
          <span className="truncate text-base font-semibold text-foreground" title={team.name}>
            {team.name}
          </span>
        </div>
        {badge ? (
          <span className="shrink-0 rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {badge}
          </span>
        ) : null}
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          <StatusTile count={team.onlineCount} label={'online'} tone="text-success" />
          <StatusTile count={team.staleCount} label={'stale'} tone="text-warning" />
          <StatusTile count={team.offlineCount} label={'offline'} tone="text-destructive" />
        </div>
        <span className="font-semibold text-foreground">
          {team.deviceCount} {team.deviceCount === 1 ? 'device' : 'devices'}
        </span>
      </div>
    </div>
  );
}

function StatusTile({ count, label, tone }: { count: number; label: string; tone: string }) {
  return (
    <span className={`inline-flex items-center gap-1 ${tone}`}>
      <span className="font-semibold">{count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}
